import React from 'react';
import { CheckIcon } from '../../icons/CheckIcon';
import { Modal } from '../../ui/Modal';
import { EndCallIcon } from '../../icons/EndCallIcon';
import { useConversationPalette } from '../../conversation/ConversationTheme';

interface EndInterviewModalProps {
  open?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  questionsComplete: boolean;
  /** Opted-in responses continue collecting after media stops. */
  continuesInText?: boolean;
  /** Required-video responses keep collecting only after another video join. */
  requiresVideoToContinue?: boolean;
}

export const EndInterviewModal: React.FC<EndInterviewModalProps> = ({ open = true, onConfirm, onCancel, questionsComplete, continuesInText = false, requiresVideoToContinue = false }) => {
  const palette = useConversationPalette();
  return (
    <Modal open={open} onClose={onCancel}>
      <div className="text-center space-y-6">
        {/* Icon */}
        <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-2 ${questionsComplete
          ? palette ? 'bg-wash-accent' : 'bg-success/20'
          : 'bg-danger/20'
          }`}>
          {continuesInText ? <EndCallIcon className="w-8 h-8 text-foreground" /> : questionsComplete ? (
            <CheckIcon className={`w-8 h-8 ${palette ? 'text-accent-soft' : 'text-success'}`} />
          ) : (
            <svg className="w-8 h-8 text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}
        </div>

        {/* Content */}
        <div>
          <h3 className="text-xl font-bold text-foreground mb-2">End call?</h3>
          {continuesInText ? <p className="text-muted-foreground">{requiresVideoToContinue ? 'Rejoin the video call to continue.' : 'You can continue in text.'}</p> : questionsComplete ? (
            <p className="text-muted-foreground">
              Great job! It looks like you've covered the key questions.
              Ready to wrap up?
            </p>
          ) : (
            <p className="text-muted-foreground">
              <span className="text-danger font-semibold">You may not be done yet.</span>
              <br /><br />
              Ending early might mean missing valuable details.
              Are you sure you want to end now?
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={onConfirm}
            className={`block w-full font-bold py-3 px-6 rounded-lg transition-all transform hover:scale-[1.02] ${questionsComplete
              ? palette ? 'button-accent' : 'bg-success hover:bg-success text-success-foreground'
              : 'bg-danger hover:bg-danger text-danger-foreground'
              }`}
          >
            {questionsComplete || continuesInText ? 'End call' : 'End anyway'}
          </button>

          <button
            onClick={onCancel}
            className="block w-full bg-surface hover:bg-surface-hover text-foreground font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Continue
          </button>
        </div>
      </div>
    </Modal>
  );
};
